import AttributeRow from '@/components/AttributeRow';
import AttributeRowImage from '@/components/AttributeRowImage';
import ContentSection, { ContentSectionFlushContent, ContentSectionHeader } from '@/components/ContentSection';
import Filter from '@/components/Filter';
import { getGenderSportCoverImageSrc } from '@/components/GenderSportCoverImage';
import Page from '@/components/Page';
import PageHeader from '@/components/PageHeader';
import PageLayout from '@/components/PageLayout';
import { SectionHeaderSize } from '@/components/SectionHeader';
import { TableCell, TableHeaderCell, TableLinkRow, TableRow } from '@/components/Table';
import TeamSubNav from '@/components/subNavs/TeamSubNav';
import ProfileImage from 'components/ProfileImage';
import ApplicationShareButton from 'containers/ApplicationContainer/components/ApplicationShareButton';
import { rubyQueries, TeamPlayersShowQuery, TeamPlayersShowQueryVariables } from 'queries';
import { useEffect, useState } from 'react';
import { entryPoint } from 'react_rails/entryPoint';
import toTitleCase from 'helpers/toTitleCase';
import LocalizedDate from 'components/LocalizedDate';
import TextLink from '@/components/TextLink';
import TeamImage from 'components/TeamImage';
import styles from './styles/Show.module.scss';
import ScrollableTable from '@/components/ScrollableTable';
import { useApplicationContainer } from 'containers/ApplicationContainer';
import MinuteMediaAd from '@/components/ads/MinuteMediaAd';
import useScreenSize from 'hooks/useScreenSize';
import StnVideoAd from '@/components/ads/StnVideoAd';
import FilterSection from '@/components/FilterSection';
import OutbrainAd from '@/components/ads/OutbrainAd';
import InfinitePagination from '../../../@/components/InfinitePagination';

export default entryPoint(function Show({
  query: { team, teamPlayer },
  variables,
  statCategories
}: {
  query: TeamPlayersShowQuery;
  variables: TeamPlayersShowQueryVariables;
  statCategories?: Record<string, Record<string, string>>;
}) {
  const [, { updateCurrentQueryStringParams, getCurrentQueryStringParam }] = useApplicationContainer();
  const categoryFromUrl = getCurrentQueryStringParam('category');
  const defaultCategory = statCategories
    ? (teamPlayer.stat && 'primary_stat_group' in teamPlayer.stat && teamPlayer.stat.primary_stat_group) || Object.keys(statCategories)[0]
    : undefined;
  const [selectedStatCategory, setSelectedStatCategory] = useState(categoryFromUrl || defaultCategory);
  const stats = statCategories && selectedStatCategory ? statCategories[selectedStatCategory] : undefined;
  const { isSmallScreen } = useScreenSize();
  const { team_player_seasons } = teamPlayer;
  const [gamePlayers, setGamePlayers] = useState(teamPlayer.gamePlayers.nodes);

  useEffect(() => {
    setSelectedStatCategory(categoryFromUrl || defaultCategory);
  }, [categoryFromUrl, defaultCategory]);

  return (
    <Page subNav={<TeamSubNav team={team} isTeamPlayerPage />}>
      <PageLayout
        sidebar={
          <>
            <MinuteMediaAd placement="SIDEBAR" />
            <ContentSection order={0}>
              <ContentSectionHeader right={<ApplicationShareButton />}>Player Details</ContentSectionHeader>
              <AttributeRowImage
                src={teamPlayer.player.coverImage || team.coverImage || getGenderSportCoverImageSrc(team.genderSport.key)}
              />
              {teamPlayer.player.classYear && <AttributeRow title="Graduation Year">{teamPlayer.player.classYear}</AttributeRow>}
              {teamPlayer.position && <AttributeRow title="Primary Position">{teamPlayer.position.name}</AttributeRow>}
              {teamPlayer.secondaryPosition && <AttributeRow title="Secondary Position">{teamPlayer.secondaryPosition.name}</AttributeRow>}
              {teamPlayer.player.heightText && <AttributeRow title="Height">{teamPlayer.player.heightText}</AttributeRow>}
              {teamPlayer.player.weightText && <AttributeRow title="Weight">{teamPlayer.player.weightText}</AttributeRow>}
            </ContentSection>
            <MinuteMediaAd placement="SIDEBAR_2" />
          </>
        }
        header={
          <ContentSection className={styles.Header}>
            <ProfileImage src={teamPlayer.player.image} className={styles.ProfileImage} genderSport={team.genderSport.slug} />
            <PageHeader>
              {teamPlayer.jerseyNumber && <span className={styles.JerseyNumber}>{teamPlayer.jerseyNumber}</span>}
              {teamPlayer.player.name}
            </PageHeader>
          </ContentSection>
        }
      >
        {!isSmallScreen && (
          <div style={{ width: '100%' }}>
            <StnVideoAd placement="INLINE" />
          </div>
        )}
        <ContentSection order={1}>
          <ContentSectionHeader size={SectionHeaderSize.LARGE_MEDIUM}>{variables.schoolYear} Game Log</ContentSectionHeader>

          <FilterSection>
            {statCategories && Object.keys(statCategories).length > 1 && (
              <Filter
                options={Object.keys(statCategories)}
                pathExtractor={category => updateCurrentQueryStringParams({ season: variables.schoolYear, category })}
                value={selectedStatCategory}
                labelExtractor={category => toTitleCase(category.replace(/_/g, ' '))}
              />
            )}
            <Filter
              pathExtractor={season => updateCurrentQueryStringParams({ season, category: selectedStatCategory })}
              value={variables.schoolYear}
              options={teamPlayer.schoolYears}
            />
          </FilterSection>
          <InfinitePagination
            onLoadMore={({
              teamPlayer: {
                gamePlayers: { nodes, pageInfo }
              }
            }) => {
              setGamePlayers(gamePlayers => [...gamePlayers, ...nodes]);
              return pageInfo;
            }}
            pageInfo={teamPlayer.gamePlayers.pageInfo}
            query={rubyQueries.teamPlayersShowQuery}
            ttl={60}
            variables={{ ...variables, paginating: true }}
          >
            <ContentSectionFlushContent>
              <ScrollableTable
                header={
                  <>
                    <TableHeaderCell />
                    {stats &&
                      Object.values(stats).map(category => (
                        <TableHeaderCell key={category} align="right">
                          {category}
                        </TableHeaderCell>
                      ))}
                  </>
                }
              >
                {gamePlayers.map(({ id, game, opponent, stat }) => (
                  <TableLinkRow href={game.webPath} key={id}>
                    <TableCell>
                      <TextLink href={game.webPath}>
                        <LocalizedDate format="MM/D" date={game.date} />
                        <span className={styles.Location}>{opponent.isHome ? '@' : 'vs'}</span>
                        <TeamImage src={opponent.team.image} alt={opponent.team.name} className={styles.TeamImage} />
                        {opponent.team.abbrev}
                      </TextLink>
                    </TableCell>
                    {stats &&
                      Object.keys(stats).map(category => (
                        <TableCell key={category} align="right">
                          {stat[category as keyof typeof stat] ?? '-'}
                        </TableCell>
                      ))}
                  </TableLinkRow>
                ))}
                {stats && teamPlayer.stat && (
                  <TableRow className={styles.Footer}>
                    <TableCell>TOTALS</TableCell>
                    {Object.keys(stats).map(category => (
                      <TableCell key={category} align="right">
                        {teamPlayer.stat![category as keyof typeof teamPlayer.stat] ?? '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </ScrollableTable>
            </ContentSectionFlushContent>
          </InfinitePagination>
          {team_player_seasons.length > 0 && (
            <ContentSection order={2} className={styles.SeasonBySeasonSection}>
              <ContentSectionHeader size={SectionHeaderSize.LARGE_MEDIUM}>Season by Season Stats</ContentSectionHeader>
              <ContentSectionFlushContent>
                <ScrollableTable
                  header={
                    <>
                      <TableHeaderCell>Season</TableHeaderCell>
                      {stats &&
                        Object.values(stats).map(category => (
                          <TableHeaderCell key={category} align="right">
                            {category}
                          </TableHeaderCell>
                        ))}
                    </>
                  }
                >
                  {team_player_seasons.map(({ season, stat }) => {
                    const statRecord = stat as Record<string, any>;
                    return (
                      <TableRow key={season.full_name}>
                        <TableCell>{season.full_name}</TableCell>
                        {stats &&
                          Object.keys(stats).map(category => (
                            <TableCell key={category} align="right">
                              {statRecord && statRecord[category] !== null ? statRecord[category] : '-'}
                            </TableCell>
                          ))}
                      </TableRow>
                    );
                  })}
                </ScrollableTable>
              </ContentSectionFlushContent>
            </ContentSection>
          )}
          <div style={{ width: '100%', paddingTop: '20px' }}>
            <MinuteMediaAd placement="IN_FEED" />
          </div>
        </ContentSection>
        <OutbrainAd />
      </PageLayout>
    </Page>
  );
});
